/**
 * Gestione utenti riutilizzabile.
 * Mode "admin-org": usa endpoint /admin/users (org-scoped server-side).
 * Mode "super-org": usa endpoint /superadmin/users (cross-org, richiede org_id esplicito).
 * Permette: create, edit (nome/ruolo), reset password, toggle active, permanent delete.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { LoadErrorBox } from "./common/LoadErrorBox";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Plus, KeyRound, Edit2, Trash2, Power, RefreshCcw, Copy, AlertTriangle, Shield,
} from "lucide-react";
import { EmptyState, Alert } from "./result/Primitives";
import { MONTHS_IT } from "./UserCardDialog";

const ROLE_LABEL = {
  super_admin: "Super admin",
  group_admin: "Admin di gruppo",
  admin: "Admin",
  operator: "Operatore",
};

export const UsersManagement = ({ mode = "admin-org", orgId, orgName, initialUsers }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState(initialUsers || []);
  const [loading, setLoading] = useState(!initialUsers);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmReset, setConfirmReset] = useState(null);
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [creds, setCreds] = useState(null);
  const [jobTitles, setJobTitles] = useState([]);

  const isSuperAdmin = currentUser?.role === "super_admin";
  const isSuperOrgMode = mode === "super-org";

  const [loadFailed, setLoadFailed] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      if (isSuperOrgMode) {
        const { data } = await api.get("/superadmin/users", { params: { org_id: orgId } });
        setUsers(data);
      } else {
        const { data } = await api.get("/admin/users");
        setUsers(data);
      }
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!initialUsers) load();
    // eslint-disable-next-line
  }, [orgId]);

  // Mansioni disponibili per il tenant (per il select nel dialog di modifica)
  useEffect(() => {
    const opts = isSuperOrgMode ? { params: { org_id: orgId } } : undefined;
    api.get("/job-titles", opts).then(({ data }) => setJobTitles(data)).catch(() => {});
    // eslint-disable-next-line
  }, [orgId]);

  // ---- handlers ----
  const onCreate = async (form) => {
    try {
      let res;
      if (isSuperOrgMode) {
        res = await api.post("/superadmin/users", { ...form, org_id: orgId });
      } else {
        res = await api.post("/admin/users", form);
      }
      setCreds({ email: res.data.email, password: res.data.temporary_password, kind: "create" });
      setCreateOpen(false);
      load();
      toast.success("Utente creato");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore");
    }
  };

  const onEdit = async (u, patch) => {
    try {
      await api.put(`/admin/users/${u.id}`, patch);
      setEditingUser(null);
      load();
      toast.success("Utente aggiornato");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore");
    }
  };

  const onResetPassword = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setCreds({ email: data.email, password: data.temporary_password, kind: "reset" });
      setConfirmReset(null);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore");
    }
  };

  const onToggleActive = async (u) => {
    try {
      if (u.active) await api.delete(`/admin/users/${u.id}`);
      else await api.put(`/admin/users/${u.id}`, { active: true });
      setConfirmToggle(null);
      load();
      toast.success(u.active ? "Utente disattivato" : "Utente riattivato");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore");
    }
  };

  const onDeletePermanent = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}/permanent`);
      setConfirmDelete(null);
      load();
      toast.success("Utente eliminato definitivamente");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-text-secondary">
          {users.length} {users.length === 1 ? "utente" : "utenti"}
          {orgName && <span className="ml-1">in {orgName}</span>}
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          data-testid="users-add-btn"
          className="bg-accent hover:bg-accent-strong text-white rounded-md h-10 px-4"
        >
          <Plus size={15} className="mr-1.5" /> Aggiungi utente
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-text-muted py-8 text-center">Caricamento…</div>
      ) : loadFailed ? (
        <LoadErrorBox what="gli utenti" onRetry={load} testId="users-load-error" />
      ) : users.length === 0 ? (
        <EmptyState title="Nessun utente" description="Aggiungi il primo utente per iniziare." />
      ) : (
        <div className="bg-surface border border-border rounded-xl overflow-hidden" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Email</th>
                <th className="text-left px-5 py-3 font-medium">Nome</th>
                <th className="text-left px-5 py-3 font-medium">Ruolo</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-left px-5 py-3 font-medium">Ultimo accesso</th>
                <th className="text-right px-5 py-3 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = currentUser?.id === u.id;
                const isSuperAdminUser = u.role === "super_admin";
                const cannotEdit = isSelf || (!isSuperAdmin && isSuperAdminUser);
                return (
                  <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.email}`}>
                    <td className="px-5 py-3 font-mono text-xs text-text-primary">
                      {u.email}
                      {isSelf && <span className="ml-2 text-[10px] uppercase tracking-wider text-text-muted">(tu)</span>}
                    </td>
                    <td className="px-5 py-3 text-text-secondary">
                      {u.full_name || "—"}
                      {u.is_service_account && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-subtle text-text-muted" data-testid={`service-badge-${u.email}`}>
                          servizio
                        </span>
                      )}
                      {u.job_title && (
                        <div className="text-[11px] text-text-muted mt-0.5" data-testid={`job-title-cell-${u.email}`}>
                          {u.job_title}{u.attendance_exempt ? " · non timbra" : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-md ${
                        u.role === "super_admin" ? "bg-brand/10 text-text-primary"
                        : u.role === "admin" ? "bg-accent-subtle text-accent-strong"
                        : "bg-subtle text-text-secondary"
                      }`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {u.active
                        ? <span className="text-xs px-2 py-0.5 rounded-md bg-success-subtle text-success-strong">Attivo</span>
                        : <span className="text-xs px-2 py-0.5 rounded-md bg-subtle text-text-muted">Disattivato</span>}
                    </td>
                    <td className="px-5 py-3 text-xs text-text-secondary">
                      {u.last_login ? new Date(u.last_login).toLocaleString("it-IT") : "Mai"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <IconBtn icon={Edit2} title="Modifica" disabled={cannotEdit} onClick={() => setEditingUser(u)} testId={`edit-${u.email}`} />
                        <IconBtn icon={KeyRound} title="Reset password" disabled={cannotEdit} onClick={() => setConfirmReset(u)} testId={`reset-${u.email}`} />
                        <IconBtn icon={Power} title={u.active ? "Disattiva" : "Riattiva"}
                          disabled={cannotEdit}
                          danger={u.active}
                          onClick={() => setConfirmToggle(u)}
                          testId={`toggle-${u.email}`} />
                        <IconBtn icon={Trash2} title="Elimina definitivamente"
                          disabled={cannotEdit}
                          danger
                          onClick={() => setConfirmDelete(u)}
                          testId={`delete-${u.email}`} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={onCreate}
        orgId={orgId}
        allowSuperAdmin={isSuperAdmin && orgId === "8energy"}
      />

      <EditUserDialog
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onSave={onEdit}
        jobTitles={jobTitles}
        allowSuperAdmin={isSuperAdmin}
      />

      <ConfirmDialog
        open={!!confirmReset}
        onClose={() => setConfirmReset(null)}
        title="Reset password"
        description={`Generare una nuova password temporanea per ${confirmReset?.email}? L'utente dovrà cambiarla al prossimo accesso.`}
        confirmLabel="Genera nuova password"
        confirmVariant="primary"
        onConfirm={() => onResetPassword(confirmReset)}
        icon={RefreshCcw}
      />

      <ConfirmDialog
        open={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        title={confirmToggle?.active ? "Disattiva utente" : "Riattiva utente"}
        description={confirmToggle?.active
          ? `${confirmToggle?.email} non potrà più accedere fino alla riattivazione. Lo storico richieste resta visibile.`
          : `Vuoi riattivare l'accesso per ${confirmToggle?.email}?`}
        confirmLabel={confirmToggle?.active ? "Disattiva" : "Riattiva"}
        confirmVariant={confirmToggle?.active ? "danger" : "primary"}
        onConfirm={() => onToggleActive(confirmToggle)}
      />

      <DeletePermanentDialog
        user={confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => onDeletePermanent(confirmDelete)}
      />

      <CredentialsDialog creds={creds} onClose={() => setCreds(null)} />
    </div>
  );
};

// ============================================================
// Sub-components
// ============================================================
const IconBtn = ({ icon: Icon, title, onClick, disabled, danger, testId }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={onClick}
    data-testid={testId}
    className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors ${
      disabled
        ? "text-text-muted/40 cursor-not-allowed"
        : danger
        ? "text-danger hover:bg-danger-subtle"
        : "text-text-secondary hover:text-text-primary hover:bg-subtle"
    }`}
  >
    <Icon size={14} />
  </button>
);

const generateTempPwd = () => {
  const chars = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#";
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

const CreateUserDialog = ({ open, onClose, onCreate, orgId, allowSuperAdmin }) => {
  const [form, setForm] = useState({ email: "", full_name: "", role: "operator", password: "" });
  useEffect(() => {
    if (open) setForm({ email: "", full_name: "", role: "operator", password: generateTempPwd() });
  }, [open]);

  const submit = (e) => {
    e.preventDefault();
    // Nota: il campo password non è inviato al backend (il backend genera sempre la sua).
    // Lo mostriamo all'admin solo come riferimento UI; il valore reale arriva via response.
    onCreate({ email: form.email, full_name: form.full_name, role: form.role });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-xl bg-surface sm:max-w-md" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo utente</DialogTitle>
          <DialogDescription>L'utente riceverà una password temporanea da cambiare al primo accesso.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-medium text-text-secondary">Email *</Label>
            <Input
              type="email"
              data-testid="new-user-email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 h-10 rounded-md border-border bg-surface"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-text-secondary">Nome completo *</Label>
            <Input
              data-testid="new-user-name"
              required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="mt-1.5 h-10 rounded-md border-border bg-surface"
              placeholder="Mario Rossi"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-text-secondary">Ruolo *</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1.5 h-10 rounded-md border-border bg-surface" data-testid="new-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="operator">Operatore</SelectItem>
                <SelectItem value="admin">Amministratore</SelectItem>
                {allowSuperAdmin && <SelectItem value="super_admin">Super amministratore (Wattline)</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <Alert variant="info">
            La password temporanea sarà mostrata UNA SOLA VOLTA dopo la creazione. Copiala e inviala all'utente.
          </Alert>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" data-testid="new-user-submit" className="bg-accent hover:bg-accent-strong text-white">
              Crea utente
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditUserDialog = ({ user, onClose, onSave, allowSuperAdmin, jobTitles = [] }) => {
  const [form, setForm] = useState({ full_name: "", role: "operator", is_service_account: false });
  const [exemptAsk, setExemptAsk] = useState(null);
  const [exemptLoading, setExemptLoading] = useState(false);
  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || "",
        role: user.role,
        is_service_account: !!user.is_service_account,
        can_clock_mobile: !!user.can_clock_mobile,
        job_title_id: user.job_title_id || "dipendente",
        phone: user.phone || "",
        birthday_day: user.birthday_day ? String(user.birthday_day) : "",
        birthday_month: user.birthday_month ? String(user.birthday_month) : "",
        birthday_year: user.birthday_year ? String(user.birthday_year) : "",
        matricola: user.matricola || "",
        contract_daily_hours: user.contract_daily_hours ?? 8,
        attendance_exempt: !!user.attendance_exempt,
        exempt_touched: false,
      });
      setExemptAsk(null);
    }
  }, [user]);
  if (!user) return null;

  const toggleExempt = async (v) => {
    if (!v) {
      setForm((f) => ({ ...f, attendance_exempt: false, exempt_touched: true }));
      setExemptAsk(null);
      return;
    }
    setExemptLoading(true);
    try {
      const { data } = await api.get(`/attendance/exempt-preview?user_id=${user.id}`);
      setExemptAsk(data);
    } catch {
      // modulo presenze non attivo per il tenant: applica senza anteprima
      setForm((f) => ({ ...f, attendance_exempt: true, exempt_touched: true }));
    } finally {
      setExemptLoading(false);
    }
  };

  const selectedJt = jobTitles.find((j) => j.id === form.job_title_id);

  const save = () => {
    if ((form.birthday_day && !form.birthday_month) || (!form.birthday_day && form.birthday_month)) {
      toast.error("Compleanno: giorno e mese vanno indicati insieme.");
      return;
    }
    if (form.birthday_year && !form.birthday_day) {
      toast.error("Compleanno: indica anche giorno e mese.");
      return;
    }
    onSave(user, {
      full_name: form.full_name,
      role: form.role,
      is_service_account: form.is_service_account,
      can_clock_mobile: !!form.can_clock_mobile,
      job_title_id: form.job_title_id,
      matricola: (form.matricola || "").trim() || null,
      contract_daily_hours:
        form.contract_daily_hours === "" || form.contract_daily_hours == null
          ? null
          : Number(form.contract_daily_hours),
      ...(form.exempt_touched ? { attendance_exempt: !!form.attendance_exempt } : {}),
      phone: (form.phone || "").trim() || null,
      birthday_day: form.birthday_day ? Number(form.birthday_day) : null,
      birthday_month: form.birthday_month ? Number(form.birthday_month) : null,
      birthday_year: form.birthday_year ? Number(form.birthday_year) : null,
    });
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-xl bg-surface sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Modifica utente</DialogTitle>
          <DialogDescription className="font-mono text-xs">{user.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-medium text-text-secondary">Nome completo</Label>
            <Input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="mt-1.5 h-10 rounded-md border-border bg-surface"
              data-testid="edit-user-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-text-secondary">Ruolo (accessi)</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5 h-10 rounded-md border-border bg-surface" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operatore</SelectItem>
                  <SelectItem value="admin">Amministratore</SelectItem>
                  {allowSuperAdmin && <SelectItem value="super_admin">Super amministratore</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-text-secondary">Mansione</Label>
              <Select
                value={form.job_title_id}
                onValueChange={(v) => {
                  const jt = jobTitles.find((j) => j.id === v);
                  setForm((f) => ({
                    ...f,
                    job_title_id: v,
                    ...(f.exempt_touched ? {} : { attendance_exempt: jt ? !jt.clocks_in : f.attendance_exempt }),
                  }));
                }}
              >
                <SelectTrigger className="mt-1.5 h-10 rounded-md border-border bg-surface" data-testid="edit-user-job-title"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {jobTitles.map((jt) => (
                    <SelectItem key={jt.id} value={jt.id} data-testid={`job-title-option-${jt.id}`}>
                      {jt.name}{jt.clocks_in ? "" : " · non timbra"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {selectedJt && !selectedJt.clocks_in && (
            <div className="text-[11px] text-text-muted bg-subtle border border-border rounded-md px-3 py-2" data-testid="edit-user-no-clock-hint">
              Mansione senza timbratura: niente widget timbratura, escluso dai conteggi presenze e da «Chi c'è oggi». Compare tra i «Collaboratori».
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-text-secondary">N° matricola</Label>
              <Input
                value={form.matricola || ""}
                onChange={(e) => setForm({ ...form, matricola: e.target.value })}
                maxLength={30}
                placeholder="es. 001"
                className="mt-1.5 h-10 rounded-md border-border bg-surface"
                data-testid="edit-user-matricola"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-text-secondary">Ore contrattuali/giorno</Label>
              <Input
                type="number"
                min={0.5}
                max={24}
                step={0.5}
                value={form.contract_daily_hours ?? ""}
                onChange={(e) => setForm({ ...form, contract_daily_hours: e.target.value })}
                className="mt-1.5 h-10 rounded-md border-border bg-surface"
                data-testid="edit-user-contract-hours"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-text-secondary">Telefono</Label>
            <Input
              value={form.phone || ""}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              maxLength={30}
              placeholder="+39 …"
              className="mt-1.5 h-10 rounded-md border-border bg-surface"
              data-testid="edit-user-phone"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-text-secondary">Compleanno (anno facoltativo)</Label>
            <div className="flex gap-2 mt-1.5">
              <select
                value={form.birthday_day || ""}
                onChange={(e) => setForm({ ...form, birthday_day: e.target.value })}
                className="h-10 rounded-md border border-border bg-surface text-sm px-2 flex-1"
                data-testid="edit-user-birthday-day"
              >
                <option value="">Giorno</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select
                value={form.birthday_month || ""}
                onChange={(e) => setForm({ ...form, birthday_month: e.target.value })}
                className="h-10 rounded-md border border-border bg-surface text-sm px-2 flex-1"
                data-testid="edit-user-birthday-month"
              >
                <option value="">Mese</option>
                {MONTHS_IT.slice(1).map((m, i) => (
                  <option key={m} value={i + 1}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                ))}
              </select>
              <Input
                type="number"
                min={1900}
                max={new Date().getFullYear()}
                value={form.birthday_year || ""}
                onChange={(e) => setForm({ ...form, birthday_year: e.target.value })}
                placeholder="Anno"
                className="h-10 rounded-md border-border bg-surface flex-1"
                data-testid="edit-user-birthday-year"
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border bg-subtle px-4 py-3">
            <div className="min-w-0 pr-3">
              <div className="text-sm font-medium text-text-primary">Account di servizio</div>
              <div className="text-[11px] text-text-muted leading-relaxed">
                Casella tecnica/condivisa: esclusa da «Chi c'è oggi», dal riepilogo presenze e dai conteggi del team.
              </div>
            </div>
            <Switch
              checked={form.is_service_account}
              onCheckedChange={(v) => setForm({ ...form, is_service_account: v })}
              data-testid="edit-user-service-account"
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border bg-subtle px-4 py-3">
            <div className="min-w-0 pr-3">
              <div className="text-sm font-medium text-text-primary">Può timbrare da mobile</div>
              <div className="text-[11px] text-text-muted leading-relaxed">
                Di norma la timbratura si fa solo dal computer. Attivalo per chi lavora fuori sede.
              </div>
            </div>
            <Switch
              checked={!!form.can_clock_mobile}
              onCheckedChange={(v) => setForm({ ...form, can_clock_mobile: v })}
              data-testid="edit-user-clock-mobile"
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border bg-subtle px-4 py-3">
            <div className="min-w-0 pr-3">
              <div className="text-sm font-medium text-text-primary">Non timbra</div>
              <div className="text-[11px] text-text-muted leading-relaxed">
                Esenzione personale dalla timbratura: prevale sulla mansione. Chi non timbra non compare mai come assente — né negli export, né nei conteggi, né in «Chi c'è oggi».
              </div>
            </div>
            <Switch
              checked={!!form.attendance_exempt}
              disabled={exemptLoading}
              onCheckedChange={toggleExempt}
              data-testid="edit-user-exempt-switch"
            />
          </div>
          {exemptAsk && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 space-y-2" data-testid="exempt-confirm-panel">
              <div className="text-sm text-amber-900 leading-relaxed">
                <b data-testid="exempt-days-count">{exemptAsk.days_reclassified}</b> giornate passate risultavano «Assente» solo per mancata timbratura e verranno riclassificate: non saranno più conteggiate come assenze, nemmeno negli export dei mesi scorsi. Le ferie, i permessi e le malattie già registrate non si toccano.
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" type="button" variant="outline" onClick={() => setExemptAsk(null)} data-testid="exempt-cancel-btn">
                  Annulla
                </Button>
                <Button
                  size="sm"
                  type="button"
                  className="bg-accent hover:bg-accent-strong text-white"
                  onClick={() => {
                    setForm((f) => ({ ...f, attendance_exempt: true, exempt_touched: true }));
                    setExemptAsk(null);
                  }}
                  data-testid="exempt-confirm-btn"
                >
                  Conferma
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} className="bg-accent hover:bg-accent-strong text-white" data-testid="edit-user-submit">
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ConfirmDialog = ({ open, onClose, title, description, confirmLabel, confirmVariant, onConfirm, icon: Icon }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="rounded-xl bg-surface sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {Icon && <Icon size={18} />}
          {title}
        </DialogTitle>
        <DialogDescription className="text-sm text-text-secondary leading-relaxed">{description}</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Annulla</Button>
        <Button
          onClick={onConfirm}
          data-testid="confirm-action-btn"
          className={confirmVariant === "danger"
            ? "bg-danger hover:bg-danger-strong text-white"
            : "bg-accent hover:bg-accent-strong text-white"}
        >
          {confirmLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const DeletePermanentDialog = ({ user, onClose, onConfirm }) => {
  const [confirmText, setConfirmText] = useState("");
  useEffect(() => { setConfirmText(""); }, [user?.id]);
  if (!user) return null;
  const expected = "ELIMINA";
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-xl bg-surface sm:max-w-md" data-testid="delete-permanent-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-danger-strong">
            <AlertTriangle size={18} />
            Elimina definitivamente
          </DialogTitle>
          <DialogDescription>Questa operazione è irreversibile.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Alert variant="danger">
            Stai per eliminare definitivamente <strong>{user.email}</strong>. È consentito solo se l'utente ha 0 richieste storiche. Altrimenti usa <strong>Disattiva</strong>.
          </Alert>
          <div>
            <Label className="text-xs font-medium text-text-secondary">Scrivi <span className="font-mono">{expected}</span> per confermare</Label>
            <Input
              data-testid="delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="mt-1.5 h-10 rounded-md border-border bg-surface font-mono"
              placeholder={expected}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button
            disabled={confirmText !== expected}
            onClick={onConfirm}
            data-testid="delete-confirm-btn"
            className="bg-danger hover:bg-danger-strong text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 size={14} className="mr-1.5" /> Elimina definitivamente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CredentialsDialog = ({ creds, onClose }) => {
  if (!creds) return null;
  const copy = () => {
    navigator.clipboard.writeText(`Email: ${creds.email}\nPassword: ${creds.password}`);
    toast.success("Credenziali copiate");
  };
  const sendEmail = () => {
    toast.warning("Email non configurata", { description: "Copia le credenziali e inviale manualmente per ora." });
  };
  return (
    <Dialog open={!!creds} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-xl bg-surface sm:max-w-md" data-testid="creds-dialog">
        <DialogHeader>
          <DialogTitle>{creds.kind === "reset" ? "Password resettata" : "Utente creato"}</DialogTitle>
          <DialogDescription>Salva queste credenziali ora. Non saranno più visibili.</DialogDescription>
        </DialogHeader>
        <Alert variant="warning">
          <strong>Una sola volta!</strong> Copia o invia subito. Al prossimo accesso l'utente dovrà cambiare la password.
        </Alert>
        <div className="bg-subtle rounded-md p-4 font-mono text-sm space-y-1.5 my-3" data-testid="creds-block">
          <div><span className="text-text-muted text-xs">Email:</span><br/> {creds.email}</div>
          <div><span className="text-text-muted text-xs">Password:</span><br/> <span className="text-text-primary font-semibold">{creds.password}</span></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={copy} data-testid="creds-copy"><Copy size={14} className="mr-1.5" /> Copia</Button>
          <Button variant="outline" onClick={sendEmail} data-testid="creds-send-email"><Shield size={14} className="mr-1.5" /> Invia per email</Button>
          <Button onClick={onClose} className="bg-accent hover:bg-accent-strong text-white" data-testid="creds-close">Ho salvato</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
